import React, { useCallback, useEffect, useId, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import AssetDetailSection from './AssetDetailSection';

interface CreateImagesSectionProps {
    featuredImage: File | null;
    additionalImages: File[];
    onFeaturedChange: (file: File | null) => void;
    onAdditionalChange: (files: File[]) => void;
}

const CreateImagesSection: React.FC<CreateImagesSectionProps> = ({
    featuredImage,
    additionalImages,
    onFeaturedChange,
    onAdditionalChange,
}) => {
    const { t } = useTranslation();
    // <label htmlFor>, not programmatic input.click(): iOS Safari silently
    // ignores synthetic clicks on display:none file inputs in some contexts
    // (same fix as ImageSlot / ImagesSection). Inputs are sr-only.
    const featuredInputId = useId();
    const additionalInputId = useId();
    const [isDragging, setIsDragging] = useState(false);
    const [isFeaturedDragging, setIsFeaturedDragging] = useState(false);

    const handleFeaturedDrop = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsFeaturedDragging(false);
        const imageFile = Array.from(e.dataTransfer.files ?? []).find((f) => f.type.startsWith('image/'));
        if (imageFile) onFeaturedChange(imageFile);
    };
    const handleFeaturedDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsFeaturedDragging(true);
    };
    const handleFeaturedDragLeave = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsFeaturedDragging(false);
    };

    const featuredPreview = useMemo(
        () => (featuredImage ? URL.createObjectURL(featuredImage) : null),
        [featuredImage],
    );
    useEffect(() => {
        return () => {
            if (featuredPreview) URL.revokeObjectURL(featuredPreview);
        };
    }, [featuredPreview]);

    const additionalPreviews = useMemo(
        () => additionalImages.map((f) => ({ file: f, url: URL.createObjectURL(f) })),
        [additionalImages],
    );
    useEffect(() => {
        return () => {
            additionalPreviews.forEach((p) => URL.revokeObjectURL(p.url));
        };
    }, [additionalPreviews]);

    const handleFeaturedSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0] ?? null;
        onFeaturedChange(file);
        e.target.value = '';
    };

    const handleAdditionalSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!e.target.files) return;
        onAdditionalChange([...additionalImages, ...Array.from(e.target.files)]);
        e.target.value = '';
    };

    const removeAdditional = useCallback(
        (index: number) => onAdditionalChange(additionalImages.filter((_, i) => i !== index)),
        [additionalImages, onAdditionalChange],
    );

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(false);
        if (e.dataTransfer.files) {
            const imageFiles = Array.from(e.dataTransfer.files).filter((f) => f.type.startsWith('image/'));
            if (imageFiles.length > 0) {
                onAdditionalChange([...additionalImages, ...imageFiles]);
            }
        }
    };

    return (
        <AssetDetailSection title={t('asset_detail.sections.images')}>
            {/* Featured image */}
            <div className={'mb-4'}>
                <span className={'text-sm text-foreground/60 font-medium'}>
                    {t('asset_create.images.featured_label')} *
                </span>

                {featuredPreview ? (
                    <div
                        className={`relative mt-2 w-fit rounded-lg transition-all ${
                            isFeaturedDragging ? 'ring-2 ring-primary ring-offset-2' : ''
                        }`}
                        onDrop={handleFeaturedDrop}
                        onDragOver={handleFeaturedDragOver}
                        onDragLeave={handleFeaturedDragLeave}
                    >
                        <img
                            src={featuredPreview}
                            alt={t('asset_detail.fields.featured_image')}
                            className={'h-40 w-auto rounded-lg object-cover pointer-events-none'}
                        />
                        <span className={'absolute top-2 left-2 bg-black/50 text-white text-xs px-2 py-0.5 rounded'}>
                            {t('asset_detail.fields.featured_image')}
                        </span>
                        <button
                            type={'button'}
                            onClick={() => onFeaturedChange(null)}
                            className={'absolute top-2 right-2 bg-red-600 hover:bg-red-700 text-white'
                                + ' rounded-full w-7 h-7 flex items-center justify-center text-sm shadow-md'}
                            title={t('asset_detail.actions.remove')}
                        >
                            &times;
                        </button>
                        <label
                            htmlFor={featuredInputId}
                            className={'absolute bottom-2 right-2 bg-primary text-white cursor-pointer'
                                + ' rounded-full w-7 h-7 flex items-center justify-center text-xs shadow-md'}
                            title={t('asset_detail.images.replace_featured')}
                        >
                            &#x270E;
                        </label>
                    </div>
                ) : (
                    <label
                        htmlFor={featuredInputId}
                        className={'mt-2 w-40 h-40 border-2 border-dashed rounded-lg flex flex-col'
                            + ' items-center justify-center cursor-pointer transition-colors '
                            + (isFeaturedDragging
                                ? 'border-primary bg-primary/5'
                                : 'border-foreground/20 hover:border-foreground/40')}
                        onDrop={handleFeaturedDrop}
                        onDragOver={handleFeaturedDragOver}
                        onDragLeave={handleFeaturedDragLeave}
                    >
                        <span className={'text-2xl text-foreground/30'}>+</span>
                        <span className={'text-xs text-foreground/60 text-center px-2 font-medium'}>
                            {t('asset_create.images.add_featured')}
                        </span>
                        <span className={'text-[10px] text-foreground/40 text-center px-2 mt-0.5'}>
                            {t('asset_create.images.drop_or_click_short')}
                        </span>
                        <span className={'text-[10px] text-foreground/40 text-center px-2'}>
                            {t('asset_detail.images.max_size')}
                        </span>
                    </label>
                )}
                <input
                    id={featuredInputId}
                    type={'file'}
                    accept={'image/*'}
                    onChange={handleFeaturedSelect}
                    className={'sr-only'}
                />
            </div>

            {/* Additional images */}
            <div>
                <span className={'text-sm text-foreground/60 font-medium'}>
                    {t('asset_create.images.additional_label')}
                </span>

                {additionalPreviews.length > 0 && (
                    <div className={'mt-2 grid grid-cols-4 gap-2'}>
                        {additionalPreviews.map((p, i) => (
                            <div key={p.url} className={'relative group aspect-square overflow-hidden rounded-lg'}>
                                <img
                                    src={p.url}
                                    alt={`${t('asset_detail.sections.images')} ${i + 1}`}
                                    className={'w-full h-full object-cover'}
                                />
                                <button
                                    type={'button'}
                                    onClick={() => removeAdditional(i)}
                                    className={'absolute top-1 right-1 bg-red-600 hover:bg-red-700 text-white'
                                        + ' rounded-full w-6 h-6 flex items-center justify-center text-xs'
                                        + ' opacity-100 lg:opacity-0 lg:group-hover:opacity-100 transition-opacity shadow-md'}
                                    title={t('asset_detail.actions.remove')}
                                >
                                    &times;
                                </button>
                            </div>
                        ))}
                    </div>
                )}

                {/* Drop zone */}
                <label
                    htmlFor={additionalInputId}
                    onDrop={handleDrop}
                    onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                    onDragLeave={(e) => { e.preventDefault(); setIsDragging(false); }}
                    className={`block mt-3 border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors ${
                        isDragging
                            ? 'border-primary bg-primary/5'
                            : 'border-foreground/20 hover:border-foreground/40'
                    }`}
                >
                    <p className={'text-sm text-foreground/60 font-medium'}>
                        {t('asset_create.images.add_additional')}
                    </p>
                    <p className={'text-xs text-foreground/40 mt-0.5'}>
                        {t('asset_create.images.drop_or_click_short')}
                    </p>
                    <p className={'text-xs text-foreground/40'}>
                        {t('asset_detail.images.max_size')}
                    </p>
                </label>
                <input
                    id={additionalInputId}
                    type={'file'}
                    accept={'image/*'}
                    multiple
                    onChange={handleAdditionalSelect}
                    className={'sr-only'}
                />
            </div>
        </AssetDetailSection>
    );
};

export default CreateImagesSection;
